'use client';

import { SubmitEvent, useState } from 'react';
import { asText, Content } from '@prismicio/client';
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons';

import {
  Box,
  Button,
  RadioGroup,
  Typography,
  RadioGroupOption,
  RadioGroupState,
} from '@preventx/oxygen';

// Types
import { ProductVariantOption } from '@/types/products/productVariants';

// Contexts
import { useProductDetailsContext } from '@/context/ProductDetailsProvider';

type WithProductVariantsProps = {
  options: ProductVariantOption[];
  slice: Content.ProductsHeroHeadlineSliceWithProductVariants;
};

export const WithProductVariants = ({
  slice,
  options,
}: WithProductVariantsProps) => {
  const { product, setProduct, onNavigate } = useProductDetailsContext();

  const { primary } = slice;
  const {
    select_product_label,
    product_variant_label,
    product_selection_help_message,
  } = primary;

  const [validate, setValidate] = useState<boolean>(false);

  const onSubmit = (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!product.isValid) {
      setValidate(true);

      return;
    }

    const option = options.find(({ id }) => id === product.radioValue);

    if (!option) {
      return;
    }

    const { productKey, application } = option;

    onNavigate({ productKey: productKey, application: application });
  };

  return (
    <Box addFlow component="form" onSubmit={onSubmit}>
      <RadioGroup
        state={product}
        addQuestionTitle
        variant="bordered"
        validate={validate}
        onChange={setProduct}
        id="product-selection"
        name="product-selection"
        setValidate={setValidate}
        validationRules={{ required: true }}
        label={asText(product_variant_label)}
        options={options.map((option): RadioGroupOption => {
          const { id, label, price } = option;

          return {
            id: id,
            value: id,

            label: (
              <label htmlFor={id} className="has-details repel">
                <Typography component="span">{label}</Typography>
                <Typography component="strong">{price}</Typography>
              </label>
            ),
          };
        })}
        infoMessages={[
          {
            position: 'between-input-label',
            content: (
              <Typography component="small">
                {asText(product_selection_help_message)}
              </Typography>
            ),
          },
        ]}
      />

      <Button
        size="lg"
        colour="primary"
        variant="solid"
        icon={{
          position: 'right',
          definition: faArrowRight,
          addDirectionalIcon: true,
        }}
      >
        {asText(select_product_label)}
      </Button>
    </Box>
  );
};
